import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { isNotNullOrUndefined, productProperty, System } from 'ngx-core-services';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { getLabels, buildPsc100Properties, getStatusDetails } from './psc100-dashboard.utils';
import { ColumnConfig, ColumnTypeEnum } from 'ngx-zcs-components';
import { selectSelectedPsc100, selectedSystemUid, SystemsState } from '../../store';

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-psc100-dashboard',
  templateUrl: './psc100-dashboard.component.html',
  styleUrls: ['./psc100-dashboard.component.scss'],
})
export class Psc100DashboardComponent implements ICustomBlock, IReduxBlock {
  @ViewChild('psc100ActionsTemplate', { static: true }) psc100ActionsTemplate!: TemplateRef<any>;
  @Output() onDeviceSelected = new EventEmitter<string>();

  readonly _externalProperties$ = new BehaviorSubject<productProperty[] | undefined>(undefined);
  @Input() set externalDevices(properties: productProperty[] | undefined) {
    this._externalProperties$.next(properties);
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';
  @Input() productImageSrc?: string;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
    @Input() system?: System;

  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
    this.updateColumns();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  pcsInvertersColumns: ColumnConfig[] = [];

  readonly selectedPsc100$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._refresh$,
  ]).pipe(
    switchMap(([deviceId, _systemId]) => this.store.select(selectSelectedPsc100(deviceId))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly statusPsc100$: Observable<productProperty | undefined> = combineLatest([
    this.selectedPsc100$,
    this.labels$,
  ]).pipe(
    map(([device, labels]) => {
      if (!device) {
        return undefined;
      }
      const { label, cssClasses } = getStatusDetails(device.connected, labels);
      return {
        name: labels.status,
        value: label,
        icon: 'info',
        iconCssClasses: 'text-grey-400',
        cssClasses,
      } as productProperty;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly selectedPsc100Properties$: Observable<productProperty[]> = combineLatest([
    this.selectedPsc100$,
    this._externalProperties$.asObservable(),
    this.labels$,
  ]).pipe(
    map(([device, externalProps, labels]) => {
      if (!device) {
        return externalProps ?? [];
      }
      const base = buildPsc100Properties(device, labels);
      return externalProps ? [...base, ...externalProps] : base;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly pcsInverters$ = this.selectedPsc100$.pipe(
    map((device) => device?.inverters ?? []),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
  ) {
    this.updateColumns();
  }

  private updateColumns(): void {
    this.pcsInvertersColumns = [
      {
        columnDef: 'serial',
        header: this.labels.serialNumber,
        type: ColumnTypeEnum.Text,
        cell: (row: { sn?: string }) => row.sn ?? '',
      },
      {
        columnDef: 'deviceType',
        header: this.labels.deviceType,
        type: ColumnTypeEnum.Text,
        cell: () => this.labels.inverter,
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.psc100ActionsTemplate,
      },
    ];
  }
}
